
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  CustomsPackingItem,
  HqPackingListItem,
  ContainerInfoModel,
  isOverUnderageBlocked,
} from "./configContainerTypeModels";
import {formatPrice} from "@/utils/number";
import ConfigContainerPackingDetailDrawer from "./ConfigContainerPackingDetailDrawer.vue";

@Component({
  name: "ConfigContainerPackingBlock",
  components: { ConfigContainerPackingDetailDrawer },
})
export default class ConfigContainerPackingBlock extends Vue {
  @Prop({ required: true }) private data!: HqPackingListItem;
  @Prop({ required: false, default: "editable" })
  private mode!: "editable" | "readonly" | "configured";
  @Prop({ required: false, default: () => [] })
  private configuredContainers!: ContainerInfoModel[];
  @Prop({ required: false, default: true }) private expanded!: boolean;
  /** 父级勾选变更计数，用于强制同步表头状态 */
  @Prop({ required: false, default: 0 }) private selectionTick!: number;

  private headerChecked = false;
  private headerIndeterminate = false;
  private detailVisible = false;
  private activePacking: CustomsPackingItem | null = null;

  @Watch("selectionTick")
  private onSelectionTickChange() {
    this.syncHeaderCheckbox();
  }

  @Watch("data", { deep: true, immediate: true })
  private onDataChange() {
    this.syncHeaderCheckbox();
  }

  private get showCheckbox(): boolean {
    return this.mode !== "readonly";
  }

  private get showContainerType(): boolean {
    return this.mode !== "editable";
  }

  private get topLevelItems(): CustomsPackingItem[] {
    return this.data.customsPackingItems || [];
  }

  /** 配置柜型时，超欠发主包装不可勾选 */
  private canSelect(row: CustomsPackingItem): boolean {
    if (this.mode !== "editable") {
      return true;
    }
    return !isOverUnderageBlocked(row.overUnderageStatus);
  }

  private get selectableItems(): CustomsPackingItem[] {
    return this.topLevelItems.filter((item) => this.canSelect(item));
  }

  private syncHeaderCheckbox() {
    const items = this.selectableItems;
    const selected = items.filter((i) => !!i.select).length;
    this.headerChecked = items.length > 0 && selected === items.length;
    this.headerIndeterminate = selected > 0 && selected < items.length;
    this.$nextTick(() => {
      const el = this.$refs.headerCheckbox as HTMLInputElement | undefined;
      if (el) {
        el.checked = this.headerChecked;
        el.indeterminate = this.headerIndeterminate;
      }
    });
  }

  private setRowSelect(row: CustomsPackingItem, checked: boolean) {
    this.$set(row, "select", checked);
  }

  private get remarkText(): string {
    const remark = this.data.remark;
    if (!remark || !Array.isArray(remark)) {
      return "";
    }
    return remark
      .filter((r) => r && r.remark)
      .map((r) => `${r.remark}:${r.value}`)
      .join(", ");
  }

  /** PLT / CTN 按主包装编号前缀统计 */
  private get pltCount(): number {
    return this.topLevelItems.filter((item) =>
      String(item.code || "").trim().toUpperCase().startsWith("PLT")
    ).length;
  }

  private get ctnCount(): number {
    return this.topLevelItems.filter((item) =>
      String(item.code || "").trim().toUpperCase().startsWith("CTN")
    ).length;
  }

  private get secondaryCtnQuantity(): number {
    return this.topLevelItems.reduce(
      (sum, item) => sum + Number(item.totalSecondaryCtnQuantity || 0),
      0
    );
  }

  private get productQuantity(): number {
    return this.topLevelItems.reduce(
      (sum, item) => sum + Number(item.totalProductQuantity || 0),
      0
    );
  }

  private toggleExpanded() {
    this.$emit("update:expanded", !this.expanded);
  }

  private onToggleAll(checked: boolean) {
    this.selectableItems.forEach((item) => this.setRowSelect(item, checked));
    this.syncHeaderCheckbox();
    this.$emit("selection-change");
  }

  private onToggleRow(row: CustomsPackingItem, checked: boolean) {
    if (!this.canSelect(row)) {
      return;
    }
    this.setRowSelect(row, checked);
    this.syncHeaderCheckbox();
    this.$emit("selection-change");
  }

  private containerLabel(configuredContainerId: string): string {
    if (!configuredContainerId) {
      return "-";
    }
    const found = (this.configuredContainers || []).find(
      (c) => c.id === configuredContainerId
    );
    if (!found) {
      return "-";
    }
    return `${found.name}-${found.containerTypeCode}`;
  }

  private productNames(
    row: CustomsPackingItem,
    key: "description" | "declarationDescription"
  ): string {
    const products = row.productInfoItems || [];
    return Array.from(
      new Set(products.map((p) => p[key]).filter(Boolean))
    ).join(",");
  }

  private totalPrice(row: CustomsPackingItem): string {
    const products = row.productInfoItems || [];
    let totalPrice =  products
      .map((p) => Number(p.unitPrice || 0) * Number(p.totalNumberPcs || 0))
      .reduce((pre, cur) => pre + cur, 0);
    return formatPrice(totalPrice);
  }

  private packingCode(row: CustomsPackingItem): string {
    return String(row.code || "")
      .split("\n")[0]
      .trim();
  }

  private packingMarks(row: CustomsPackingItem): Array<"over" | "short"> {
    if (row.overUnderageStatus === "OVERDELIVERY") {
      return ["over"];
    }
    if (row.overUnderageStatus === "UNDERDELIVERY") {
      return ["short"];
    }
    if (row.overUnderageStatus === "ALL") {
      return ["over", "short"];
    }
    return [];
  }

  private openDetail(row: CustomsPackingItem) {
    this.activePacking = row;
    this.detailVisible = true;
  }
}
